// Typed calls for the operations the admin web uses (apps/fire/contracts/openapi.yaml, /fer/v1 only).
import type { ApiClient, QueryValue, WithEtag } from "./client";
import { ApiProblem } from "./problem";
import type * as T from "./types";

/** Safety cap when a screen needs every row: more pages than this is reported, never cut silently. */
export const MAX_PAGES = 20;
export const PAGE_SIZE = 200;

function truncated(): ApiProblem {
  return new ApiProblem({
    type: "urn:lcc:problem:list_truncated",
    title: "List truncated",
    status: 0,
    code: "list_truncated",
    detail: `More than ${MAX_PAGES * PAGE_SIZE} records match.`,
  });
}

/** Follows `next_cursor` to the end, or reports `list_truncated` (never cut silently). */
async function fetchAll<R>(
  api: ApiClient,
  path: string,
  query: Record<string, QueryValue>,
  pathParams?: Record<string, string>,
  pageSize = PAGE_SIZE,
): Promise<R[]> {
  const items: R[] = [];
  let cursor: string | null | undefined = undefined;
  for (let i = 0; i < MAX_PAGES; i++) {
    const page: T.Page<R> = await api.get<T.Page<R>>(path, { pathParams, query: { ...query, limit: pageSize, cursor } });
    items.push(...(Array.isArray(page?.items) ? page.items : []));
    cursor = page?.page?.next_cursor;
    if (!cursor) return items;
  }
  throw truncated();
}

const id = (name: string, value: string) => ({ [name]: value });
/** Search text is sent only when it meets the contract's minimum length (2). */
const search = (q: string | undefined) => (q && q.trim().length >= 2 ? q.trim() : undefined);

export interface ApprovalsResult {
  items: T.Proposal[];
  reports: T.ReportAwaitingApproval[];
}

export const endpoints = {
  // --- configuration and files
  getConfig: (api: ApiClient) => api.get<T.StaffFireConfig | T.PublicFireConfig>("/fer/v1/config"),
  getFileDownload: (api: ApiClient, fileId: string) =>
    api.get<T.FileDownload>("/fer/v1/files/{file_id}/download", { pathParams: id("file_id", fileId) }),

  // --- intake
  listAlerts: (api: ApiClient, q: { status?: T.AlertStatus[]; channel?: T.Channel[]; incidentId?: string; from?: string; to?: string } = {}) =>
    fetchAll<T.AlertSummary>(api, "/fer/v1/alerts", {
      status: q.status,
      channel: q.channel,
      incident_id: q.incidentId,
      received_from: q.from,
      received_to: q.to,
    }),
  createAlert: (api: ApiClient, body: T.AlertInput, idempotencyKey: string) =>
    api.post<T.Alert>("/fer/v1/alerts", { body, idempotencyKey }),
  getAlert: (api: ApiClient, alertId: string) => api.get<T.Alert>("/fer/v1/alerts/{alert_id}", { pathParams: id("alert_id", alertId) }),
  claimAlert: (api: ApiClient, alertId: string, body: T.ClaimBody) =>
    api.post<T.Alert>("/fer/v1/alerts/{alert_id}/claim", { pathParams: id("alert_id", alertId), body }),
  setAlertLocation: (api: ApiClient, alertId: string, body: T.SetLocationBody) =>
    api.put<T.Alert>("/fer/v1/alerts/{alert_id}/location", { pathParams: id("alert_id", alertId), body }),
  sendLocationLink: (api: ApiClient, alertId: string, body: T.LinkSendBody, idempotencyKey: string) =>
    api.post<T.LocationLink>("/fer/v1/alerts/{alert_id}/location-links", { pathParams: id("alert_id", alertId), body, idempotencyKey }),
  recordCallback: (api: ApiClient, alertId: string, body: T.CallbackBody) =>
    api.post<T.Alert>("/fer/v1/alerts/{alert_id}/callbacks", { pathParams: id("alert_id", alertId), body }),
  confirmAlert: (api: ApiClient, alertId: string, body: T.ConfirmBody, idempotencyKey: string) =>
    api.post<T.Incident>("/fer/v1/alerts/{alert_id}/confirmation", { pathParams: id("alert_id", alertId), body, idempotencyKey }),
  linkAlert: (api: ApiClient, alertId: string, body: T.LinkBody) =>
    api.post<T.Alert>("/fer/v1/alerts/{alert_id}/link", { pathParams: id("alert_id", alertId), body }),
  dismissAlert: (api: ApiClient, alertId: string, body: T.DismissBody) =>
    api.post<T.Alert>("/fer/v1/alerts/{alert_id}/dismissal", { pathParams: id("alert_id", alertId), body }),
  reopenAlert: (api: ApiClient, alertId: string, body: T.ReopenBody) =>
    api.post<T.Alert>("/fer/v1/alerts/{alert_id}/reopening", { pathParams: id("alert_id", alertId), body }),
  listRefusedSos: (api: ApiClient) => fetchAll<T.RefusedSos>(api, "/fer/v1/refused-sos", {}),

  // --- maps
  searchPlaces: (api: ApiClient, q: string, near?: [number, number]) =>
    api.get<T.PlacesResult>("/fer/v1/places", { query: { q: search(q), near: near ? `${near[0]},${near[1]}` : undefined, limit: 10 } }),
  lookupWard: (api: ApiClient, lon: number, lat: number, accuracyM?: number) =>
    api.get<T.WardLookup>("/fer/v1/wards/lookup", { query: { lon, lat, accuracy_m: accuracyM } }),
  getWardMap: (api: ApiClient) => api.get<T.WardFeatureCollection>("/fer/v1/maps/wards"),
  getLiveMap: (api: ApiClient) => api.get<T.LiveMap>("/fer/v1/maps/live"),

  // --- incidents and dispatch
  listIncidents: (
    api: ApiClient,
    q: {
      status?: T.IncidentStatus[];
      incidentType?: string;
      severity?: T.Severity[];
      wardId?: string;
      backupRequested?: boolean;
      from?: string;
      to?: string;
      reportStatus?: T.ReportStatus;
    } = {},
  ) =>
    fetchAll<T.IncidentSummary>(api, "/fer/v1/incidents", {
      status: q.status,
      incident_type: q.incidentType,
      severity: q.severity,
      ward_id: q.wardId,
      backup_requested: q.backupRequested,
      created_from: q.from,
      created_to: q.to,
      report_status: q.reportStatus,
    }),
  getIncident: (api: ApiClient, incidentId: string) =>
    api.get<T.Incident>("/fer/v1/incidents/{incident_id}", { pathParams: id("incident_id", incidentId) }),
  updateIncident: (api: ApiClient, incidentId: string, body: T.IncidentUpdate) =>
    api.patch<T.Incident>("/fer/v1/incidents/{incident_id}", { pathParams: id("incident_id", incidentId), body }),
  escalateIncident: (api: ApiClient, incidentId: string, body: T.EscalationInput) =>
    api.post<T.Incident>("/fer/v1/incidents/{incident_id}/escalation", { pathParams: id("incident_id", incidentId), body }),
  containIncident: (api: ApiClient, incidentId: string, body: T.ContainmentBody) =>
    api.post<T.Incident>("/fer/v1/incidents/{incident_id}/containment", { pathParams: id("incident_id", incidentId), body }),
  cancelIncident: (api: ApiClient, incidentId: string, body: T.CancelIncidentBody) =>
    api.post<T.CancellationResult>("/fer/v1/incidents/{incident_id}/cancellation", { pathParams: id("incident_id", incidentId), body }),
  getSuggestions: (api: ApiClient, incidentId: string, capabilities?: string[]) =>
    api.get<T.Suggestions>("/fer/v1/incidents/{incident_id}/suggestions", {
      pathParams: id("incident_id", incidentId),
      query: { capabilities: capabilities && capabilities.length ? capabilities : undefined, limit: 20 },
    }),
  assignAppliances: (api: ApiClient, incidentId: string, body: T.AssignBody, idempotencyKey: string) =>
    api.post<T.AssignResult>("/fer/v1/incidents/{incident_id}/dispatches", { pathParams: id("incident_id", incidentId), body, idempotencyKey }),
  getDispatch: (api: ApiClient, dispatchId: string) =>
    api.get<T.Dispatch>("/fer/v1/dispatches/{dispatch_id}", { pathParams: id("dispatch_id", dispatchId) }),
  recordDispatchStatus: (api: ApiClient, dispatchId: string, body: T.DispatchStatusInput) =>
    api.post<T.Dispatch>("/fer/v1/dispatches/{dispatch_id}/status", { pathParams: id("dispatch_id", dispatchId), body }),
  standDown: (api: ApiClient, dispatchId: string, body: T.StandDownBody) =>
    api.post<T.Dispatch>("/fer/v1/dispatches/{dispatch_id}/stand-down", { pathParams: id("dispatch_id", dispatchId), body }),

  // --- incident reports
  getReport: (api: ApiClient, incidentId: string): Promise<WithEtag<T.IncidentReport>> =>
    api.requestWithEtag<T.IncidentReport>("GET", { path: "/fer/v1/incidents/{incident_id}/report", pathParams: id("incident_id", incidentId) }),
  decideReport: (api: ApiClient, incidentId: string, body: T.ReportDecisionBody) =>
    api.post<T.IncidentReport>("/fer/v1/incidents/{incident_id}/report/decision", { pathParams: id("incident_id", incidentId), body }),

  // --- resources
  listStations: (api: ApiClient, q: { active?: boolean } = {}) => fetchAll<T.Station>(api, "/fer/v1/stations", { active: q.active }),
  createStation: (api: ApiClient, body: T.StationInput, idempotencyKey: string) =>
    api.post<T.Station>("/fer/v1/stations", { body, idempotencyKey }),
  getStation: (api: ApiClient, stationId: string) =>
    api.get<T.StationDetail>("/fer/v1/stations/{station_id}", { pathParams: id("station_id", stationId) }),
  updateStation: (api: ApiClient, stationId: string, body: T.StationUpdate) =>
    api.patch<T.Station>("/fer/v1/stations/{station_id}", { pathParams: id("station_id", stationId), body }),
  listAppliances: (api: ApiClient, q: { stationId?: string; status?: T.ApplianceStatus[]; capability?: string } = {}) =>
    fetchAll<T.Appliance>(api, "/fer/v1/appliances", { station_id: q.stationId, status: q.status, capability: q.capability }),
  createAppliance: (api: ApiClient, body: T.ApplianceInput, idempotencyKey: string) =>
    api.post<T.Appliance>("/fer/v1/appliances", { body, idempotencyKey }),
  getAppliance: (api: ApiClient, applianceId: string) =>
    api.get<T.Appliance>("/fer/v1/appliances/{appliance_id}", { pathParams: id("appliance_id", applianceId) }),
  updateAppliance: (api: ApiClient, applianceId: string, body: T.ApplianceUpdate) =>
    api.patch<T.Appliance>("/fer/v1/appliances/{appliance_id}", { pathParams: id("appliance_id", applianceId), body }),
  setServiceStatus: (api: ApiClient, applianceId: string, body: T.ServiceStatusBody) =>
    api.post<T.Appliance>("/fer/v1/appliances/{appliance_id}/service-status", { pathParams: id("appliance_id", applianceId), body }),
  listAssignments: (api: ApiClient, q: { stationId?: string; partyId?: string; includeEnded?: boolean } = {}) =>
    fetchAll<T.StationAssignment>(api, "/fer/v1/station-assignments", {
      station_id: q.stationId,
      party_id: q.partyId,
      include_ended: q.includeEnded,
    }),
  createAssignment: (api: ApiClient, body: T.AssignmentBody, idempotencyKey: string) =>
    api.post<T.StationAssignment>("/fer/v1/station-assignments", { body, idempotencyKey }),
  endAssignment: (api: ApiClient, assignmentId: string) =>
    api.delete<T.StationAssignment>("/fer/v1/station-assignments/{assignment_id}", { pathParams: id("assignment_id", assignmentId) }),

  // --- prevention: premises
  listPremises: (api: ApiClient, q: { wardId?: string; occupancyType?: string; riskClass?: string; certificateStatus?: string; q?: string } = {}) =>
    fetchAll<T.Premises>(api, "/fer/v1/premises", {
      ward_id: q.wardId,
      occupancy_type: q.occupancyType,
      risk_class: q.riskClass,
      certificate_status: q.certificateStatus,
      q: search(q.q),
    }),
  registerPremises: (api: ApiClient, body: T.PremisesInput, idempotencyKey: string) =>
    api.post<T.Premises>("/fer/v1/premises", { body, idempotencyKey }),
  getPremises: (api: ApiClient, premisesId: string) =>
    api.get<T.PremisesDetail>("/fer/v1/premises/{premises_id}", { pathParams: id("premises_id", premisesId) }),
  updatePremises: (api: ApiClient, premisesId: string, body: T.PremisesUpdate) =>
    api.patch<T.Premises>("/fer/v1/premises/{premises_id}", { pathParams: id("premises_id", premisesId), body }),
  changeRiskClass: (api: ApiClient, premisesId: string, body: T.RiskClassBody, idempotencyKey: string) =>
    api.post<T.Proposal>("/fer/v1/premises/{premises_id}/risk-class", { pathParams: id("premises_id", premisesId), body, idempotencyKey }),
  changeOccupancy: (api: ApiClient, premisesId: string, body: T.OccupancyBody, idempotencyKey: string) =>
    api.post<T.Proposal>("/fer/v1/premises/{premises_id}/occupancy", { pathParams: id("premises_id", premisesId), body, idempotencyKey }),
  changeOwner: (api: ApiClient, premisesId: string, body: T.OwnerBody, idempotencyKey: string) =>
    api.post<T.Proposal>("/fer/v1/premises/{premises_id}/owner", { pathParams: id("premises_id", premisesId), body, idempotencyKey }),

  // --- prevention: applications and bills
  listApplications: (api: ApiClient, q: { status?: T.ApplicationStatus[]; wardId?: string; premisesId?: string } = {}) =>
    fetchAll<T.CertificateApplication>(api, "/fer/v1/certificate-applications", { status: q.status, ward_id: q.wardId, premises_id: q.premisesId }),
  applyForCertificate: (api: ApiClient, body: T.ApplyBody, idempotencyKey: string) =>
    api.post<T.CertificateApplication>("/fer/v1/certificate-applications", { body, idempotencyKey }),
  getApplication: (api: ApiClient, applicationId: string) =>
    api.get<T.CertificateApplication>("/fer/v1/certificate-applications/{application_id}", { pathParams: id("application_id", applicationId) }),
  checkoutFee: (api: ApiClient, applicationId: string, idempotencyKey: string) =>
    api.post<T.Checkout>("/fer/v1/certificate-applications/{application_id}/checkout", { pathParams: id("application_id", applicationId), idempotencyKey }),
  cancelBill: (api: ApiClient, applicationId: string, body: T.BillCancellationBody, idempotencyKey: string) =>
    api.post<T.Proposal>("/fer/v1/certificate-applications/{application_id}/bill-cancellation", {
      pathParams: id("application_id", applicationId),
      body,
      idempotencyKey,
    }),
  requestReinspection: (api: ApiClient, applicationId: string, body: T.ReinspectionBody, idempotencyKey: string) =>
    api.post<T.CertificateApplication>("/fer/v1/certificate-applications/{application_id}/reinspection-request", {
      pathParams: id("application_id", applicationId),
      body,
      idempotencyKey,
    }),

  // --- prevention: inspections, held items, certificates
  listInspections: (api: ApiClient, q: { premisesId?: string; applicationId?: string; wardId?: string; outcome?: T.InspectionOutcome; from?: string; to?: string } = {}) =>
    fetchAll<T.Inspection>(api, "/fer/v1/inspections", {
      premises_id: q.premisesId,
      application_id: q.applicationId,
      ward_id: q.wardId,
      outcome: q.outcome,
      from: q.from,
      to: q.to,
    }),
  getInspection: (api: ApiClient, inspectionId: string) =>
    api.get<T.Inspection>("/fer/v1/inspections/{inspection_id}", { pathParams: id("inspection_id", inspectionId) }),
  listHeldItems: (api: ApiClient, q: { status?: "held_for_review" | "accepted" | "rejected"; wardId?: string } = {}) =>
    fetchAll<T.HeldItem>(api, "/fer/v1/field/held-items", { status: q.status, ward_id: q.wardId }),
  reviewHeldItem: (api: ApiClient, heldId: string, body: T.HeldReviewBody) =>
    api.post<T.HeldItem>("/fer/v1/field/held-items/{held_id}/review", { pathParams: id("held_id", heldId), body }),
  listCertificates: (api: ApiClient, q: { status?: T.CertificateStatus; expiringWithinDays?: number; wardId?: string; premisesId?: string } = {}) =>
    fetchAll<T.Certificate>(api, "/fer/v1/certificates", {
      status: q.status,
      expiring_within_days: q.expiringWithinDays,
      ward_id: q.wardId,
      premises_id: q.premisesId,
    }),
  getCertificate: (api: ApiClient, certificateId: string) =>
    api.get<T.Certificate>("/fer/v1/certificates/{certificate_id}", { pathParams: id("certificate_id", certificateId) }),
  revokeCertificate: (api: ApiClient, certificateId: string, body: T.RevocationBody, idempotencyKey: string) =>
    api.post<T.Proposal>("/fer/v1/certificates/{certificate_id}/revocation", { pathParams: id("certificate_id", certificateId), body, idempotencyKey }),

  // --- hydrants
  listHydrants: (api: ApiClient, q: { bbox?: string; wardId?: string; condition?: T.HydrantCondition[]; surveyDue?: boolean } = {}) =>
    fetchAll<T.Hydrant>(api, "/fer/v1/hydrants", { bbox: q.bbox, ward_id: q.wardId, condition: q.condition, survey_due: q.surveyDue }, undefined, 2000),
  registerHydrant: (api: ApiClient, body: T.HydrantInput, idempotencyKey: string) =>
    api.post<T.Hydrant>("/fer/v1/hydrants", { body, idempotencyKey }),
  getHydrant: (api: ApiClient, hydrantId: string) =>
    api.get<T.HydrantDetail>("/fer/v1/hydrants/{hydrant_id}", { pathParams: id("hydrant_id", hydrantId) }),
  updateHydrant: (api: ApiClient, hydrantId: string, body: T.HydrantUpdate) =>
    api.patch<T.Hydrant>("/fer/v1/hydrants/{hydrant_id}", { pathParams: id("hydrant_id", hydrantId), body }),
  surveyHydrant: (api: ApiClient, hydrantId: string, body: T.HydrantSurveyInput, idempotencyKey: string) =>
    api.post<T.HydrantDetail>("/fer/v1/hydrants/{hydrant_id}/surveys", { pathParams: id("hydrant_id", hydrantId), body, idempotencyKey }),

  // --- local sign-in
  getLocalAuthStatus: (api: ApiClient) => api.get<T.LocalAuthStatus>("/fer/v1/local-auth/status", { anonymous: true }),
  localSignIn: (api: ApiClient, body: T.LocalSignInBody) => api.post<T.LocalToken>("/fer/v1/local-auth/token", { body, anonymous: true }),
  listMyCredentials: (api: ApiClient) => api.get<{ items: T.LocalCredential[] }>("/fer/v1/me/local-credentials"),
  enrolCredential: (api: ApiClient, body: T.EnrolBody) => api.post<T.LocalEnrolment>("/fer/v1/me/local-credentials", { body }),
  removeMyCredential: (api: ApiClient, credentialId: string) =>
    api.delete<void>("/fer/v1/me/local-credentials/{credential_id}", { pathParams: id("credential_id", credentialId) }),
  listCredentials: (api: ApiClient, q: { partyId?: string; locked?: boolean } = {}) =>
    fetchAll<T.LocalCredential>(api, "/fer/v1/local-credentials", { party_id: q.partyId, locked: q.locked }),
  resetCredential: (api: ApiClient, credentialId: string, body: T.ResetCredentialBody) =>
    api.post<T.LocalCredential>("/fer/v1/local-credentials/{credential_id}/reset", { pathParams: id("credential_id", credentialId), body }),

  // --- approvals
  listApprovals: async (api: ApiClient, q: { mine?: "to_confirm" | "proposed" | "all"; kind?: T.ProposalKind; status?: T.ProposalStatus } = {}): Promise<ApprovalsResult> => {
    const items: T.Proposal[] = [];
    let reports: T.ReportAwaitingApproval[] = [];
    let cursor: string | null | undefined = undefined;
    for (let i = 0; i < MAX_PAGES; i++) {
      const page: T.ApprovalsPage = await api.get<T.ApprovalsPage>("/fer/v1/approvals", {
        query: { mine: q.mine, kind: q.kind, status: q.status, limit: PAGE_SIZE, cursor },
      });
      items.push(...(Array.isArray(page?.items) ? page.items : []));
      if (i === 0) reports = Array.isArray(page?.reports) ? page.reports : [];
      cursor = page?.page?.next_cursor;
      if (!cursor) return { items, reports };
    }
    throw truncated();
  },
  getApproval: (api: ApiClient, proposalId: string) =>
    api.get<T.Proposal>("/fer/v1/approvals/{proposal_id}", { pathParams: id("proposal_id", proposalId) }),

  // --- reports
  getResponseTimes: (api: ApiClient, q: { from: string; to: string; groupBy?: string; severity?: T.Severity[] }) =>
    api.get<T.ResponseTimeReport>("/fer/v1/reports/response-times", { query: { from: q.from, to: q.to, group_by: q.groupBy, severity: q.severity } }),
  getSnapshot: (api: ApiClient) => api.get<T.ReportSnapshot>("/fer/v1/reports/snapshot"),
};
